'use client';

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { RefreshCw } from 'lucide-react';
import { motion, useMotionValue, useTransform, animate } from 'framer-motion';

interface PullToRefreshProps {
    children: React.ReactNode;
    onRefresh?: () => Promise<void>;
    threshold?: number;
    className?: string;
}

export default function PullToRefresh({
    children,
    onRefresh,
    threshold = 80,
    className = '',
}: PullToRefreshProps) {
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [isPulling, setIsPulling] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const startY = useRef(0);
    const currentY = useRef(0);

    const pullDistance = useMotionValue(0);
    const opacity = useTransform(pullDistance, [0, threshold], [0, 1]);
    const scale = useTransform(pullDistance, [0, threshold], [0.5, 1]);
    const rotate = useTransform(pullDistance, [0, threshold * 2], [0, 360]);
    const contentY = useTransform(pullDistance, [0, threshold * 1.5], [0, 60]);

    // Check if we're in standalone PWA mode
    const [isPWA, setIsPWA] = useState(false);

    useEffect(() => {
        const standalone = window.matchMedia('(display-mode: standalone)').matches;
        const isIOSPWA = (window.navigator as any).standalone === true;
        setIsPWA(standalone || isIOSPWA);
    }, []);

    const handleRefresh = useCallback(async () => {
        setIsRefreshing(true);

        try {
            if (onRefresh) {
                await onRefresh();
            } else {
                // Default behavior: reload the page
                window.location.reload();
            }
        } catch (error) {
            console.error('Refresh failed:', error);
        } finally {
            setIsRefreshing(false);
            animate(pullDistance, 0, { duration: 0.3 });
        }
    }, [onRefresh, pullDistance]);

    const handleTouchStart = useCallback((e: React.TouchEvent) => {
        // Only trigger if at top of page
        if (window.scrollY > 0) return;

        startY.current = e.touches[0].clientY;
        setIsPulling(true);
    }, []);

    const handleTouchMove = useCallback((e: React.TouchEvent) => {
        if (!isPulling || isRefreshing) return;
        if (window.scrollY > 0) {
            setIsPulling(false);
            pullDistance.set(0);
            return;
        }

        currentY.current = e.touches[0].clientY;
        const diff = currentY.current - startY.current;

        // Only pull down, not up
        if (diff > 0) {
            // Apply resistance to make it feel more natural
            const resistance = 0.5;
            const distance = Math.min(diff * resistance, threshold * 1.5);
            pullDistance.set(distance);

            // Prevent default scroll when pulling
            if (distance > 10) {
                e.preventDefault();
            }
        }
    }, [isPulling, isRefreshing, pullDistance, threshold]);

    const handleTouchEnd = useCallback(() => {
        if (!isPulling) return;

        setIsPulling(false);
        const distance = pullDistance.get();

        if (distance >= threshold && !isRefreshing) {
            handleRefresh();
        } else {
            animate(pullDistance, 0, { duration: 0.3 });
        }
    }, [isPulling, pullDistance, threshold, isRefreshing, handleRefresh]);

    // Only show on mobile and in PWA mode (or always on mobile for better UX)
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 768);
        };
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Don't render pull-to-refresh functionality on desktop
    if (!isMobile) {
        return <>{children}</>;
    }

    return (
        <div
            ref={containerRef}
            className={`relative ${className}`}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
        >
            {/* Pull indicator */}
            <motion.div
                className="fixed left-1/2 -translate-x-1/2 z-[100] pointer-events-none"
                style={{
                    top: 20,
                    opacity,
                }}
            >
                <motion.div
                    className={`
                        w-12 h-12 rounded-full 
                        bg-gradient-to-br from-amber-400/90 to-orange-500/90
                        backdrop-blur-sm border border-white/20
                        flex items-center justify-center
                        shadow-lg shadow-amber-500/30
                    `}
                    style={{ scale }}
                >
                    <motion.div
                        style={{ rotate: isRefreshing ? undefined : rotate }}
                        animate={isRefreshing ? { rotate: 360 } : undefined}
                        transition={isRefreshing ? { duration: 1, repeat: Infinity, ease: 'linear' } : undefined}
                    >
                        <RefreshCw
                            className={`text-white ${isRefreshing ? 'animate-spin' : ''}`}
                            size={24}
                        />
                    </motion.div>
                </motion.div>

                {/* Pull text indicator */}
                <motion.p
                    className="text-center text-xs text-white/80 mt-2 font-medium"
                    style={{ opacity }}
                >
                    {isRefreshing ? 'Refreshing...' : pullDistance.get() >= threshold ? 'Release to refresh' : 'Pull to refresh'}
                </motion.p>
            </motion.div>

            {/* Content with pull transform */}
            <motion.div
                style={{
                    y: contentY,
                }}
            >
                {children}
            </motion.div>
        </div>
    );
}
